import { getBrandUrl, type Brand } from '~/lib/brand';

export type { Brand };
export type BrandScope = Brand | 'both';

/**
 * Single authoritative ownership map for ALL brand surfaces.
 *
 * Ordered most-specific-prefix-first; the first matching prefix wins
 * (longest/earliest-match semantics via {@link ownerBrandForPath}). A path
 * absent from this list defaults to `'both'` (renders on either brand, no
 * cross-domain rewrite).
 *
 * - 'flow'   = only canonical on flow.ballee.co
 * - 'ballee' = only canonical on ballee.co (apex)
 * - 'both'   = shared surface (default; no cross-brand link rewrite)
 *
 * This is the source of truth the link-time resolver
 * (`lib/routing/resolve-app-url.ts`) and the navigation-visibility helpers
 * below both consume. `HOME_ROUTE_SCOPES` is DERIVED from the `/home/*`
 * entries here (see below) so the two never diverge — one edit surface
 * (Critical Rule #8).
 *
 * The proxy's redirect helpers (`balleeOnlyIndexableSurface`,
 * `isBalleeOnlyAdminPath`, `handleCrossBrandBalleeRoutes` in `proxy.ts`)
 * stay independently implemented as the runtime redirect safety net — the
 * data here is kept in agreement with them but does NOT derive them, so a
 * registry edit can never silently change a 301/308 redirect. See the
 * `proxy.ts` JSDoc on those helpers.
 *
 * NOTE ON ORDERING: the more-specific Ballee admin prefix
 * (`/admin/competitions`) MUST appear before the catch-all `/admin` → 'flow'
 * so the longest-prefix match resolves admin/competitions to Ballee. The
 * list below is authored in that order.
 */
export const ROUTE_BRAND_OWNERSHIP: ReadonlyArray<
  readonly [prefix: string, brand: BrandScope]
> = [
  // ── Ballee public indexable surfaces ─────────────────────────────────
  // (proxy.ts: balleeOnlyIndexableSurface 301s these off Flow)
  ['/competitions', 'ballee'],
  ['/dancers', 'ballee'],
  // Auditions browse + detail are Ballee-canonical (the public page hardcodes
  // the apex canonical); proxy.ts 301s them off Flow. Registered so the nav
  // link resolver + gate agree with the proxy (was absent → resolved 'both').
  ['/auditions', 'ballee'],

  // ── Marketplace module — lives on BOTH domains, no cross-brand redirect ─
  // Product direction 2026-07-13: the marketplace (browse + a seller's own
  // Sales/Listings) is served natively on flow.ballee.co AND ballee.co and must
  // NOT redirect on either — it displays wherever it's used. SEO dedup is
  // handled by the hardcoded canonical → ballee.co (marketplace-canonical.ts)
  // + the Flow-sitemap omission, NOT by a 301 (the balleeOnlyIndexableSurface
  // marketplace arm in proxy.ts was removed in the same change). Reverses the
  // GSC-2026-06-08 consolidation; canonical is the mitigation.
  ['/marketplace', 'both'],
  ['/home/sales', 'both'],
  ['/home/listings', 'both'],

  // ── Ballee admin surfaces (more specific than the /admin catch-all) ───
  // (proxy.ts: isBalleeOnlyAdminPath 308s these off Flow)
  ['/admin/competitions', 'ballee'],
  // Offers are part of the competitions module (scholarship/audition offers),
  // so the whole offers admin surface is Ballee, not Flow — without this it
  // fell through to the `/admin` → flow catch-all (BAL-402 finding).
  ['/admin/offers', 'ballee'],
  // Auditions are a Ballee community/discovery surface (sibling of competitions):
  // the public `/auditions/*` and `/home/auditions` routes already live on the
  // apex, so the audition admin (imports + sources) belongs on Ballee too. Without
  // these they fell through to the `/admin` → flow catch-all (2026-07-07 audit).
  ['/admin/audition-imports', 'ballee'],
  ['/admin/audition-sources', 'ballee'],
  // Marketplace admin (listing moderation) stays Ballee-only even though the
  // public `/marketplace` became a both-domain module 2026-07-13 — the
  // moderation console is reached from the Ballee admin nav only.
  ['/admin/marketplace', 'ballee'],
  // Coaching is a Ballee community feature (`/home/coaching` is Ballee-scoped).
  ['/admin/coaching', 'ballee'],
  // The /admin/support inbox was retired (support→Ballee-org, Phase 4b) — org
  // members handle support from their normal Inbox. The Support-module tabs
  // Feedback + Issues remain (Flow-linked), reachable on both admin hosts;
  // without these they fall through to the `/admin` → flow catch-all.
  ['/admin/feedback', 'both'],
  ['/admin/issues', 'both'],
  // ── Cross-cutting admin surfaces reachable on BOTH brands ─────────────
  // These run either business (comms, automation, ops, governance, taxonomy,
  // analytics, growth) so they must not fall through to the `/admin` → flow
  // catch-all. 'both' = reachable on either host, no cross-brand redirect.
  // (2026-07-07 audit; scopes confirmed by product owner.)
  ['/admin/communications', 'both'],
  ['/admin/communication-timeline', 'both'],
  ['/admin/automations', 'both'],
  // Marketing-automation group (campaigns / ab-tests / analytics) — a live
  // route group with no parent page.tsx (children carry the pages); same
  // cross-cutting comms/automation family as the two above.
  ['/admin/automation', 'both'],
  ['/admin/audit', 'both'],
  ['/admin/maintenance', 'both'],
  ['/admin/sync', 'both'],
  ['/admin/roles', 'both'],
  ['/admin/operations', 'both'],
  ['/admin/legal-compliance', 'both'],
  ['/admin/categories', 'both'],
  ['/admin/artist-analytics', 'both'],
  ['/admin/fever-ratings', 'both'],
  ['/admin/referrals', 'both'],
  ['/admin/affiliate', 'both'],
  ['/admin/repertoire', 'both'],
  // Organizations admin is served on BOTH hosts (user decision 2026-10-02): it
  // was Ballee-only, so the Partners → Organizations tab on flow.ballee.co hit
  // a bare 308 to ballee.co and dropped the admin's host-scoped session. The
  // create-organization button keeps its cross-brand handoff to the Ballee
  // form (`/home/community/organizations/create`). Not indexable (/admin is
  // noindex), so no SEO consequence.
  ['/admin/organizations', 'both'],

  // ── Ballee /home sub-routes (community/discovery features) ────────────
  ['/home/auditions', 'ballee'],
  // Offers (scholarship/audition offers) are surfaced to dancers on BOTH
  // brands — dancers on Flow are the competition-participant population and
  // were silently blocked from `/home/offers` when this was 'ballee'-only
  // (BAL-692: 96% of the 2517 dancers with offers view on Flow). The nav
  // leaf is shared between `flowDancerSurfaces`/`balleeDancerSurfaces` via
  // `offersItem` in nav-sections.ts (Critical Rule #8).
  ['/home/offers', 'both'],
  ['/home/coaching', 'ballee'],
  ['/home/explore', 'ballee'],
  ['/home/community', 'ballee'],
  // Community discovery + engagement surfaces (Ballee): saved lists, bookmarks,
  // and the ambassadors program. Registered 2026-07-13 so the menu gate hides
  // them off Flow by construction rather than by cell placement alone.
  ['/home/lists', 'ballee'],
  ['/home/bookmarks', 'ballee'],
  ['/home/ambassadors', 'ballee'],

  // ── Flow /home sub-routes (artist management features) ────────────────
  ['/home/assignments', 'flow'],
  ['/home/schedule', 'flow'],
  ['/home/events', 'flow'],
  ['/home/feedback', 'flow'],
  ['/home/invoices', 'flow'],
  ['/home/reimbursements', 'flow'],
  // Payouts is a Money surface (Flow), registered 2026-07-13 with the rest of
  // the Money group so it can't leak onto a Ballee menu.
  ['/home/payouts', 'flow'],
  ['/home/artist/contract-details', 'flow'],
  ['/home/client-user', 'flow'],

  // ── Flow admin (default): every /admin/* NOT listed Ballee above ──────
  // Placed last among the /admin prefixes so /admin/competitions wins by
  // being earlier/more-specific. Mirrors the proxy default (every admin
  // path not in BALLEE_ALLOWED_ADMIN_PATHS is Flow).
  ['/admin', 'flow'],

  // Everything else (the dashboard shell `/home`, `/auth/**`, `/welcome`,
  // `/home/artist/profile/**`, and the adaptive publics — organizations,
  // events, partners) ⇒ 'both' by absence.
];

/**
 * Resolve the owning brand for an arbitrary path via longest-/first-prefix
 * match against {@link ROUTE_BRAND_OWNERSHIP}. Absence ⇒ `'both'`.
 *
 * A prefix matches when `path` equals it exactly OR starts with `prefix/`,
 * so `/competitions` and `/competitions/x/apply` both resolve to `'ballee'`
 * but `/competitions-archive` does NOT (it is not a sub-path). Query strings
 * and hashes are ignored (only the path portion before `?`/`#` is matched).
 */
export function ownerBrandForPath(path: string): BrandScope {
  // Strip query + hash so callers can pass a full `/x?a=b#c` path.
  const pathname = path.split(/[?#]/)[0] ?? path;

  for (const [prefix, scope] of ROUTE_BRAND_OWNERSHIP) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return scope;
    }
  }

  return 'both';
}

/**
 * Single source of truth for brand-scoped /home routes.
 * Navigation config and middleware both consume this registry.
 *
 * - 'flow' = only visible on flow.ballee.co
 * - 'ballee' = only visible on ballee.co (apex)
 * - Absence from this map = visible on both brands
 *
 * DERIVED from the `/home/*` entries of {@link ROUTE_BRAND_OWNERSHIP} so the
 * two never drift. The entry set is byte-identical to the prior object
 * literal — the only change is that the data now flows from the one
 * ownership list.
 */
export const HOME_ROUTE_SCOPES: Record<string, BrandScope> = Object.fromEntries(
  ROUTE_BRAND_OWNERSHIP.filter(
    ([prefix, scope]) => prefix.startsWith('/home/') && scope !== 'both',
  ).map(([prefix, scope]) => [prefix, scope] as const),
);

/**
 * Admin routes accessible on the Ballee domain (all others are Flow-only).
 *
 * `/admin/competitions` is a Ballee-brand surface: competitions are a
 * Ballee community/discovery feature (the dancer-facing `/competitions/*`
 * and `/home/auditions` routes already live on the apex), so the admin
 * setup surface belongs here too. The proxy additionally 301/308-redirects
 * `flow.ballee.co/(admin/)competitions/*` to `ballee.co` so the surface is
 * Ballee-only, not merely Ballee-also. See `handleCrossBrandBalleeRoutes`
 * in `proxy.ts` and `docs/architecture/domain-and-seo-architecture.md`.
 *
 * The Ballee-canonical admin entry (`/admin/competitions`) is DERIVED from
 * {@link ROUTE_BRAND_OWNERSHIP}; the remaining MakerKit admin sections that
 * are reachable on Ballee but not brand-owned (accounts, users, artists,
 * shops, subscriptions, settings) stay listed explicitly — they are
 * 'both'-scoped surfaces the Ballee admin nav exposes, not Ballee-canonical
 * routes.
 *
 * `/admin/artists` (the dancer admin surface) is 'both'-scoped, NOT
 * Ballee-only: it mixes Ballee concepts (public profile fields, media,
 * ratings) with Flow operations (cast assignments, invoices, contract
 * details), so admins reach it from both brands' admin nav. It MUST be
 * reachable on ballee.co because the public dancer profile — a ballee.co
 * apex-only surface — links its super-admin "Edit profile" affordance to
 * `/admin/artists/<user_id>` on the same host (see
 * `resolveEditHref` in `components/public-dancer-view/dancer-hero.tsx`).
 * Without this entry the brand gate (`getBrandRouteBlockRedirect` in
 * `proxy.ts`) 307s that link to `/home`, so a super-admin could not edit
 * any dancer profile from the public page (BAL-399).
 */
export const BALLEE_ALLOWED_ADMIN_PATHS = [
  '/admin/accounts',
  '/admin/users',
  '/admin/artists',
  '/admin/shops',
  '/admin/subscriptions',
  '/admin/settings',
  // Ballee-canonical and shared ('both') admin surfaces, sourced from the
  // ownership registry — anything under /admin that is not Flow-only must be
  // reachable on ballee.co:
  ...ROUTE_BRAND_OWNERSHIP.filter(
    ([prefix, scope]) =>
      prefix.startsWith('/admin/') && (scope === 'ballee' || scope === 'both'),
  ).map(([prefix]) => prefix),
];

/** Check if a path (or any sub-path) is blocked for a given brand */
export function isPathBlockedForBrand(pathname: string, brand: Brand): boolean {
  // Test-only escape hatch. Playwright runs against 127.0.0.1 (no domain
  // → defaults to 'flow'), so brand-scoped routes like /home/community/feed
  // get 307'd to /home and feature-parity tests for those routes can never
  // pass. Setting PLAYWRIGHT_DISABLE_BRAND_GATING=true on the prod server
  // CI step disables the block so both Flow- and Ballee-scoped routes are
  // reachable from a single test run. Production servers don't set this
  // env var, so brand isolation stays enforced in real deployments.
  if (process.env.PLAYWRIGHT_DISABLE_BRAND_GATING === 'true') {
    return false;
  }

  return Object.entries(HOME_ROUTE_SCOPES).some(
    ([path, scope]) =>
      scope !== brand &&
      scope !== 'both' &&
      // Only a REAL cross-brand hop when the two brands resolve to different
      // origins. In dev / preview both brands share one origin, so blocking
      // here would 307 to /home with nowhere to hand off — an infinite loop
      // when /home itself routes an acting-as-client user back into this
      // route. Mirrors the same guard `isCrossBrand` uses (resolve-app-url.ts)
      // so the block gate and the handoff agree on what "cross-brand" means.
      getBrandUrl(scope) !== getBrandUrl(brand) &&
      (pathname === path || pathname.startsWith(`${path}/`)),
  );
}

/**
 * Check if a route should be visible for a given brand in navigation.
 *
 * Only `/home/*` paths are hard-gated in the menu: the proxy block gate
 * ({@link isPathBlockedForBrand}) 307s a wrong-brand `/home/*` request to
 * `/home`, so a relative menu link to one would render and then bounce. Every
 * other cross-brand surface (`/marketplace`, `/dancers`, `/competitions`, …)
 * is EXEMPT — the proxy 301/308-redirects those across brands
 * (`handleCrossBrandBalleeRoutes`), so a relative link resolves correctly
 * instead of bouncing; hiding them would strip intentionally cross-brand nav
 * (e.g. Marketplace in the Flow Pipeline, Competitions in the Flow partner
 * menu).
 *
 * Uses {@link ownerBrandForPath} (longest-PREFIX match) rather than an
 * exact-key `HOME_ROUTE_SCOPES` lookup so a child of a brand-scoped prefix
 * (e.g. `/home/client-user/settings/team` under the flow-scoped
 * `/home/client-user`) is gated too. The exact-key lookup missed those,
 * leaking Flow-only settings into the Ballee account menu — the
 * "settings pages redirect to Flow" bug. This makes the menu gate and the
 * proxy block gate agree by construction (guarded by
 * `nav-brand-gate-coherence.test.ts`).
 */
export function isRouteVisibleForBrand(path: string, brand: Brand): boolean {
  const pathname = path.split(/[?#]/)[0] ?? path;
  if (!pathname.startsWith('/home/')) return true;
  const owner = ownerBrandForPath(pathname);
  return owner === 'both' || owner === brand;
}
